import type { ReactNode } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";

import { useSession } from "../auth/AuthProvider";
import type { SessionUser } from "../auth/authApi";
import { usePickedRandom } from "../usePickedRandom";
import { ProfileMenu } from "./ProfileMenu";

/** The footer's rotating one-liner. Exported so a test can assert the footer
 * shows one of these rather than pinning the copy to whichever line the stubbed
 * `Math.random` happened to land on. */
export const BEAR_LINES = [
  "The bear does not negotiate with ransomware.",
  "Somewhere a drive is dying. Big Bear already made copies.",
  "Big Bear checks every byte. Twice.",
  "Bit rot fears the bear.",
  "Hibernation is just scheduled downtime.",
  "Every version. None of them precious.",
] as const;

/**
 * Renders one of two things depending on who's signed in — nothing at all
 * while the session is still being checked. Swapping Download for Dashboard
 * (or Sign in for the profile menu) a moment after the first paint would
 * read as having been logged out and back in again, so every session-aware
 * slot in the header and footer goes through this same guard.
 */
function BySession({
  signedIn,
  signedOut,
}: {
  signedIn: (user: SessionUser) => ReactNode;
  signedOut: ReactNode;
}) {
  const { user, checking } = useSession();
  if (checking) return null;
  return <>{user ? signedIn(user) : signedOut}</>;
}

/** Header, footer, and page shell for every page: the marketing pages
 * (`/`, `/features`, `/releases`, `/download`, `/spec`, `/privacy`, `/terms`),
 * the account pages, and — sharing this same shell — the fleet dashboard at
 * `/dashboard`. */
export function MarketingLayout() {
  // Picked once and held, so the line does not change under the reader every
  // time the shell re-renders (a session refresh, a route change).
  const bearLine = usePickedRandom(BEAR_LINES);

  return (
    <div className="marketing">
      <header className="marketing__header">
        <NavLink to="/" className="app__brand" end>
          {/* Decorative: the wordmark beside it already says the name. */}
          <img className="app__mark" src="/bear/mark.webp" alt="" />
          <span>
            Big Bear <strong>Backup</strong>
          </span>
        </NavLink>
        <nav className="marketing__nav">
          <BySession
            signedIn={() => (
              <NavLink to="/dashboard" className="button button--primary button--small">
                Dashboard
              </NavLink>
            )}
            signedOut={
              <NavLink to="/download" className="button button--primary button--small">
                Download
              </NavLink>
            }
          />
          <NavLink to="/features" className="marketing__nav__link">
            Features
          </NavLink>
          <BySession
            signedIn={(user) => <ProfileMenu user={user} />}
            signedOut={
              <NavLink to="/login" className="marketing__nav__link">
                Sign in
              </NavLink>
            }
          />
        </nav>
      </header>

      <main className="marketing__main">
        <Outlet />
      </main>

      <footer className="marketing__footer">
        <img
          className="marketing__footer__emblem"
          src="/bear/emblem-shield.webp"
          alt=""
          loading="lazy"
          decoding="async"
        />
        <div className="marketing__footer__text">
          <p className="muted">
            Big Bear Backup — encrypted, deduplicating backup for Windows 11.{" "}
            <Link to="/spec">The format is open.</Link>
          </p>
          <p className="bear-line">
            <span className="bear-line__kicker">Big Bear says</span> {bearLine}
          </p>
        </div>
        <nav>
          <NavLink to="/features">Features</NavLink>
          <NavLink to="/releases">Releases</NavLink>
          <NavLink to="/spec">Format spec</NavLink>
          <NavLink to="/providers">Providers</NavLink>
          <NavLink to="/help">Help</NavLink>
          <NavLink to="/download">Download</NavLink>
          <BySession
            signedIn={() => <NavLink to="/dashboard">Dashboard</NavLink>}
            signedOut={<NavLink to="/login">Sign in</NavLink>}
          />
          <NavLink to="/privacy">Privacy</NavLink>
          <NavLink to="/terms">Terms</NavLink>
        </nav>
      </footer>
    </div>
  );
}
